import type { CompanyIntro, PositioningItem, BusinessSectionItem } from '../types';
import { DynamicIcon } from './CommonComponents';

interface CompanyIntroSectionProps {
  data: CompanyIntro;
}

export default function CompanyIntroSection({ data }: CompanyIntroSectionProps) {
  const {
    companyName,
    companyTypes = [],
    foundedDate,
    registeredCapital,
    creditRating,
    employeeCount,
    description,
    companyPositioning = [],
    businessSectors = []
  } = data;

  const validTypes = companyTypes.filter(t => t && t.trim());

  return (
    <div>
      {/* 标题区域 */}
      <div className="px-4 py-3 border-b border-gray-100 flex items-center">
        <div className="flex items-center gap-2">
          <h3 className="text-base font-bold text-gray-900">基本情况</h3>
          <p className="text-xs text-gray-400 tracking-widest ml-1">GROUP PROFILE</p>
        </div>
      </div>

      {/* 企业名称卡片 */}
      <div className="mx-4 mb-4 mt-4 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-4 text-center">
        <div className="text-white mb-1 text-base font-bold text-gray-900 whitespace-nowrap shrink-0">
          {companyName || '企业名称'}
        </div>
        {validTypes.length > 0 && (
          <div className="text-[10px] text-blue-100">
            {validTypes.join(' · ')}
          </div>
        )}
      </div>

      {/* 信息卡片：根据数据动态展示 */}
      {(() => {
        const infoItems = [
          { value: foundedDate, label: '组建时间', color: 'text-black' },
          { value: registeredCapital, label: '注册资本', color: 'text-black' },
          { value: creditRating, label: '信用评级', color: 'text-red-500' },
          { value: employeeCount, label: '员工人数', color: 'text-black' },
        ].filter(item => item.value !== null && item.value !== undefined && String(item.value).trim());

        if (infoItems.length === 0) return null;

        const colClass =
          infoItems.length === 1 ? 'grid-cols-1' :
          infoItems.length === 2 ? 'grid-cols-2' :
          infoItems.length === 3 ? 'grid-cols-3' :
          'grid-cols-4';

        return (
          <div className="mx-4 mb-4 bg-gray-50 rounded-lg overflow-hidden">
            <div className={`grid ${colClass} py-2`}>
              {infoItems.map((item, idx) => (
                <div key={`${item.label}-${idx}`} className={`py-2 text-center ${idx > 0 ? 'border-l border-gray-200' : ''}`}>
                  <div className={`text-[16px] font-bold leading-tight ${item.color}`}>
                    {item.value}
                  </div>
                  <div className="text-[9px] text-gray-500 mt-1">{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* 企业简介 */}
      {description && (
        <div className="px-4 pb-2">
          <div className="flex items-center gap-2 mb-2 pb-2 border-b border-gray-200">
            <div className="flex items-center justify-center text-xs">
              <DynamicIcon iconName="📝" className="w-3.5 h-3.5" />
            </div>
            <h4 className="text-sm font-semibold text-gray-800">企业简介</h4>
          </div>
          <div
            className="text-xs text-gray-600 leading-relaxed rich-text-preview"
            dangerouslySetInnerHTML={{ __html: description }}
          />
        </div>
      )}

      {/* 企业定位 */}
      {companyPositioning.length > 0 && (
        <div className="px-4 pb-4">
          <div className="flex items-center gap-2 mb-2 pb-2 border-b border-gray-200">
            <div className="flex items-center justify-center text-xs">
              <DynamicIcon iconName="🎯" className="w-3.5 h-3.5" />
            </div>
            <h4 className="text-sm font-semibold text-gray-800">企业定位</h4>
          </div>
          <div className="p-2" style={{ background: 'linear-gradient(135deg, #f5f9ff 0%, #ffffff 100%)', borderRadius: '8px', border: '1px solid #e3f2fd' }}>
            <div className="flex items-center justify-center">
              {companyPositioning.map((item: PositioningItem, idx: number) => (
                <div key={item.id || `pos-${idx}`} className="flex items-center">
                  <div className="flex flex-col items-center">
                    <div className="flex items-center justify-center">
                      <DynamicIcon iconName={item.icon} className="w-9 h-9 text-blue-600" />
                    </div>
                    <span className="text-sm font-semibold text-blue-500">{item.title}</span>
                  </div>
                  {idx < companyPositioning.length - 1 && (
                    <span className="text-[14px] text-black-400 mx-3 font-light self-center">&amp;</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* 主营业务板块 */}
      {businessSectors.length > 0 && (
        <div className="px-4 pb-4">
          <div className="flex items-center gap-2 mb-2 pb-2 border-b border-gray-200">
            <div className="flex items-center justify-center text-xs">
              <DynamicIcon iconName="💼" className="w-3.5 h-3.5" />
            </div>
            <h4 className="text-sm font-semibold text-gray-800">主营业务板块</h4>
          </div>
          <div className="grid grid-cols-2" style={{ gap: '8px' }}>
            {businessSectors.map((item: BusinessSectionItem, idx: number) => (
              <div
                key={item.id || `sector-${idx}`}
                className="flex items-center"
                style={{ background: '#f8f9fa', border: '1px solid #f0f2f5', borderRadius: '6px', padding: '10px' }}
              >
                <div className="flex items-center justify-center shrink-0 mr-2"
                  style={{ width: '32px', height: '32px', borderRadius: '6px', background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)' }}
                >
                  <DynamicIcon iconName={item.icon} className="w-6 h-6 text-blue-600" />
                </div>
                <span className="text-[12px] text-[#3333333]" style={{ fontWeight: 'bold' }}>{item.title}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
